<template>
  <div class="floor-plan-editor">
    <div class="main-content-row">
      <div class="left-controls" :class="{ 'collapsed': !isLeftPanelOpen }">
        <div class="panel-header">
          <h3>泛家D</h3>
          <button class="toggle-panel-btn" @click="toggleLeftPanel" title="收起/展开左侧面板">
            {{ isLeftPanelOpen ? '收起' : '展开' }}
          </button>
        </div>
      
      <!-- Image upload section -->
      <div class="control-section">
        <h4>户型图片上传</h4>
        <div class="upload-section">
          <input 
            type="file" 
            accept="image/*" 
            @change="handleImageUpload"
            class="file-input"
          >
          <div v-if="isUploading" class="uploading-text">上传中...</div>
          <div v-if="uploadError" class="upload-error">{{ uploadError }}</div>
          <div v-if="uploadSuccess" class="upload-success">
            上传成功！图片URL：<a :href="backgroundImageUrl" target="_blank">{{ backgroundImageUrl }}</a>
          </div>
        </div>
      </div>
      
      <!-- AI Analysis section -->
      <div class="control-section">
        <h4>AI智能分析</h4>
        <div class="ai-section">
          <button
            @click="performAiAnalysis"
            :disabled="!backgroundImageUrl || isAnalyzing"
            class="btn btn-primary"
          >
            {{ isAnalyzing ? (aiPhase === 'thinking' ? '思考中...' : aiPhase === 'generating' ? '生成中...' : '分析中...') : 'AI分析' }}
          </button>

          <div v-if="isAnalyzing && aiPhase" class="ai-phase-indicator">
            <span class="phase-badge" :class="'phase-' + aiPhase">
              {{ aiPhase === 'thinking' ? '🧠 AI思考中' : '📝 内容生成中' }}
            </span>
          </div>

          <div v-if="thinkingContent" class="thinking-panel">
            <div class="thinking-header" @click="toggleThinkingPanel">
              <span>思考过程</span>
              <span class="toggle-icon">{{ isThinkingExpanded ? '▼' : '▶' }}</span>
            </div>
            <div v-if="isThinkingExpanded" ref="thinkingScroll" class="thinking-content" v-html="thinkingContentHtml"></div>
          </div>

          <div v-if="generatingContent && isAnalyzing" class="generating-preview">
            <div class="generating-header">生成内容预览</div>
            <div ref="generatingScroll" class="generating-content" v-html="generatingContentHtml"></div>
          </div>

          <div v-if="analysisError" class="upload-error">{{ analysisError }}</div>
          <div v-if="analysisSuccess" class="upload-success">
            AI分析成功！已更新设备布局和房间分析
          </div>
        </div>
      </div>
      
      <!-- Label display control -->
      <div class="control-section">
        <label class="control-checkbox">
          <input 
            type="checkbox" 
            v-model="showAllLabels"
          >
          <span>显示所有标记点文字</span>
        </label>
      </div>
      
      <!-- Device category filter control -->
      <div class="control-section">
        <h4>设备类别过滤</h4>
        <!-- Select all checkbox -->
        <label class="control-checkbox">
          <input 
            type="checkbox" 
            :checked="isAllSelected"
            :indeterminate="isIndeterminate"
            @change="handleSelectAllChange"
          >
          <span>全选设备类别</span>
        </label>
        
        <!-- Device category checkboxes -->
        <div class="device-type-checkboxes">
          <label 
            v-for="category in allCategories" 
            :key="category"
            class="control-checkbox device-type-checkbox"
          >
            <input 
              type="checkbox" 
              :checked="selectedCategories[category] || false"
              @change="handleCategoryChange(category, $event)"
            >
            <span>{{ categoryLabels[category] || category }}</span>
          </label>
        </div>
      </div>
      
      <!-- Product selection filter control -->
      <div class="control-section">
        <h4>产品选择过滤</h4>
        <label class="control-checkbox">
          <input 
            type="checkbox" 
            v-model="showOnlyDevicesWithProducts"
          >
          <span>显示已选中设备点位</span>
        </label>
      </div>
      
      <!-- Add new device control -->
      <div class="control-section">
        <h4>新增设备点位</h4>
        <div class="add-device-controls">
          <label>选择设备类型：</label>
          <select v-model="newDeviceType" class="device-type-select">
            <option value="">请选择设备类型</option>
            <option 
              v-for="(label, type) in deviceTypeLabels" 
              :key="type"
              :value="type"
            >
              {{ label }}
            </option>
          </select>
          
          <div class="stage-click-instruction" v-if="newDeviceType">
            <p>请在平面图上点击要添加设备的位置</p>
            <button @click="cancelAddDevice" class="btn btn-secondary">取消添加</button>
          </div>
        </div>
      </div>
    </div>
    
    <div class="floor-plan-content">
      <v-stage
        ref="stageRef"
        :config="{
          width: stageConfig.width,
          height: stageConfig.height,
          scaleX: scale,
          scaleY: scale,
          x: stagePosition.x,
          y: stagePosition.y,
          draggable: true
        }"
        @wheel="handleWheel"
        @click="handleStageClick"
        @dragend="handleStageDragEnd"
      >
        <v-layer ref="layerRef">
          <!-- Background image -->
          <v-image
            :config="{
              image: backgroundImage,
              x: imageConfig.x,
              y: imageConfig.y,
              width: imageConfig.width,
              height: imageConfig.height,
              opacity: 0.8
            }"
          />
          
          <!-- Smart devices -->
          <template
            v-for="(device, index) in visibleDevices"
            :key="`${device.type}-${index}`"
          >
          <!-- 1. Colored circle marker -->
          <v-circle
            :config="{
              x: imageConfig.x + device.position.x * imageConfig.width,
              y: imageConfig.y + device.position.y * imageConfig.height,
              radius: selectedItem === device ? 6 : 4,
              fill: deviceColors[device.type] || '#6c757d',
              stroke: '#fff',
              strokeWidth: selectedItem === device ? 2 : 1,
              shadowColor: 'rgba(0,0,0,0.5)',
              shadowBlur: selectedItem === device ? 3 : 2,
              shadowOffsetX: 1,
              shadowOffsetY: 1,
              cursor: selectedItem === device ? 'move' : 'pointer',
              draggable: selectedItem === device
            }"
            @click="selectDevice(device)"
            @dragstart="handleDragStart"
            @dragmove="(e) => handleDragMove(e, device)"
            @dragend="handleDragEnd"
          />

          <!-- 2. Connection line -->
          <v-line
            v-if="selectedItem === device || showAllLabels"
            :config="{
              points: [
                imageConfig.x + device.position.x * imageConfig.width,
                imageConfig.y + device.position.y * imageConfig.height,
                imageConfig.x + device.position.x * imageConfig.width + imageConfig.width * 0.03,
                imageConfig.y + device.position.y * imageConfig.height - imageConfig.height * 0.02
              ],
              stroke: selectedItem === device ? '#333' : '#666',
              strokeWidth: selectedItem === device ? 1.5 : 1,
              dash: [2, 2],
              opacity: 0.7
            }"
          />

          <!-- 3. Label Group (Background + Text) -->
          <v-group
            v-if="selectedItem === device || showAllLabels"
            :config="{
              x: imageConfig.x + device.position.x * imageConfig.width + imageConfig.width * 0.03,
              y: imageConfig.y + device.position.y * imageConfig.height - imageConfig.height * 0.02,
              listening: true,
              cursor: selectedItem === device ? 'move' : 'pointer',
              draggable: selectedItem === device
            }"
            @click="selectDevice(device)"
            @dragstart="handleDragStart"
            @dragmove="(e) => handleDragMove(e, device)"
            @dragend="handleDragEnd"
          >
            <!-- 3.1 Semi-transparent Black Background -->
            <v-rect
              :config="{
                width: (calculateTextWidth(device.name, selectedItem === device ? 13 : 11) + 15),
                height: selectedItem === device ? 24 : 20,
                x: imageConfig.width * 0.005,
                y: -(selectedItem === device ? 24 : 20) / 2 - imageConfig.height * 0.01,
                cornerRadius: 6,
                fill: 'rgba(0, 0, 0, 0.5)',
                stroke: 'rgba(255, 255, 255, 0.1)',
                strokeWidth: 1
              }"
            />

            <!-- 3.2 Device Name Text -->
            <v-text
              :config="{
                text: device.name,
                fontSize: selectedItem === device ? 13 : 11,
                fontFamily: 'Arial, sans-serif',
                fill: '#ffffff',
                x: imageConfig.width * 0.01,
                y: -imageConfig.height * 0.02,
                width: (calculateTextWidth(device.name, selectedItem === device ? 13 : 11) + 10),
                height: selectedItem === device ? 24 : 20,
                align: 'left',
                verticalAlign: 'middle',
                fontWeight: selectedItem === device ? 'bold' : 'normal',
                listening: true,
                shadowColor: 'rgba(0,0,0,0.5)',
                shadowBlur: 2,
                shadowOffsetX: 1,
                shadowOffsetY: 1
              }"
            />
          </v-group>
        </template>
          
          <!-- Tooltip removed per requirement -->
        </v-layer>
      </v-stage>

      <!-- Zoom controls -->
      <div class="zoom-controls">
        <button class="zoom-btn" @click="zoomIn" title="放大">+</button>
        <span class="zoom-level">{{ Math.round(scale * 100) }}%</span>
        <button class="zoom-btn" @click="zoomOut" title="缩小">−</button>
        <button class="zoom-btn reset-btn" @click="resetView" title="重置">重置</button>
      </div>
    </div>
    
    <div class="right-controls" :class="{ 'collapsed': !isRightPanelOpen }">
      <div class="panel-header">
        <button class="toggle-panel-btn" @click="toggleRightPanel" title="收起/展开右侧面板">
          {{ isRightPanelOpen ? '收起' : '展开' }}
        </button>
        <h4>Smart Devices</h4>
      </div>
      
      <!-- Smart Devices -->
      <div class="section">
        <div class="device-tree">
          <div
            v-for="(devices, category) in categorizedDevices"
            :key="category"
            class="category-node"
          >
            <div class="category-header">
              <h5>{{ categoryLabels[category] }}</h5>
            </div>
            
            <!-- Group devices by type -->
            <div class="type-groups">
              <div
                v-for="(typeDevices, deviceType) in groupDevicesByType(devices)"
                :key="deviceType"
                class="type-node"
              >
                <div class="type-header">
                  <span class="type-icon">▶</span>
                  <span class="type-name">{{ deviceTypeLabels[deviceType] || deviceType }}</span>
                  <span class="type-count">({{ typeDevices.length }})</span>
                </div>
                
                <div class="device-list">
                  <div
                    v-for="device in typeDevices"
                    :key="device.name"
                    class="device-item"
                    :class="{ 'selected': selectedItem === device }"
                    @click="selectDevice(device)"
                  >
                    <div class="device-header">
                      <span class="device-name">{{ device.name }}</span>
                      <button 
                        class="delete-device-btn"
                        @click.stop="deleteDevice(device)"
                        title="删除设备"
                      >
                        删除
                      </button>
                    </div>
                    <p class="device-reason">{{ device.reason }}</p>
                    <div class="device-position">
                      Position: ({{ (device.position.x * 100).toFixed(0) }}%, {{ (device.position.y * 100).toFixed(0) }}%)
                    </div>
                    
                    <!-- Product selection dropdown -->
                    <div class="product-selection">
                      <label>选择产品：</label>
                      <select 
                        v-model="selectedProducts[device.name]"
                        @change="handleProductChange(device, $event)"
                        class="product-dropdown"
                      >
                        <option value="">请选择产品</option>
                        <option 
                          v-for="product in productDatabase[device.type] || []" 
                          :key="product.id"
                          :value="product.id"
                        >
                          {{ product.brand }} - {{ product.model }} (¥{{ product.price }})
                        </option>
                      </select>
                      
                      <!-- Display selected product info -->
                      <div v-if="selectedProducts[device.name]" class="selected-product-info">
                        <div v-if="getSelectedProduct(device)" class="product-details">
                          <strong>已选产品：</strong>
                          {{ getSelectedProduct(device).brand }} {{ getSelectedProduct(device).model }}
                          <br>
                          <strong>价格：</strong>¥{{ getSelectedProduct(device).price }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

    <!-- Selected Devices Summary - Bottom Bar -->
    <div 
      class="summary-bottom-bar"
      :class="{ 'expanded': isSummaryExpanded }"
      @click="isSummaryExpanded = !isSummaryExpanded"
    >
      <div class="summary-content">
        <div class="summary-header">
          <h4>已选设备汇总</h4>
          <button class="expand-toggle" :class="{ 'expanded': isSummaryExpanded }">
            {{ isSummaryExpanded ? '收起' : '展开' }}
          </button>
        </div>
        <div class="summary-body">
          <div v-if="selectedDevicesSummary.length === 0" class="empty-summary">
            暂未选择任何设备产品
          </div>
          <div v-else class="summary-items-row">
            <!-- Left side: device list -->
            <div class="summary-list-container">
              <div class="summary-list">
                <div 
                  v-for="item in selectedDevicesSummary" 
                  :key="item.device.name"
                  class="summary-item"
                >
                  <div class="summary-device-info">
                    <span class="summary-device-name">{{ item.device.name }}</span>
                    <span class="summary-product-name">{{ item.product.brand }} {{ item.product.model }}</span>
                  </div>
                  <span class="summary-price">¥{{ item.product.price }}</span>
                </div>
              </div>
            </div>
            
            <!-- Right side: total price -->
            <div class="summary-total-container">
              <div class="summary-total">
                <span class="total-label">总计：</span>
                <span class="total-price">¥{{ totalPrice }}</span>
              </div>
              <button class="export-pdf-btn" @click.stop="exportToPdf">导出订单为PDF</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked';

export default {
  name: 'SimpleFloorPlanEditor',
  data() {
    return {
      stageConfig: {
        width: 0,
        height: 0
      },

      // 图片层配置（保持宽高比）
      imageConfig: {
        width: 0,
        height: 0,
        x: 0,
        y: 0
      },

      // 缩放与位移
      scale: 1,
      minScale: 0.1,
      maxScale: 3,
      stagePosition: { x: 0, y: 0 },

      // 原始图片尺寸
      originalImageSize: { width: 0, height: 0 },

      backgroundImage: null,
      backgroundImageUrl: 'https://iili.io/quTMhQ9.png',
      categorizedDevices: {},
      selectedItem: null,
      stageRef: null,
      layerRef: null,
      showAllLabels: false,
      showOnlyDevicesWithProducts: false,
      newDeviceType: '',
      isLeftPanelOpen: true,
      isRightPanelOpen: true,
      
      // Image upload related
      selectedFile: null,
      isUploading: false,
      uploadError: '',
      uploadSuccess: false,
      
      // ImgBB 配置
      imgHostApiKey: 'f2e8580ff9ae01f2b952ddfb235be50e',
      imgHostApiUrl: 'https://api.imgbb.com/1/upload',
      
      // AI Analysis related
      isAnalyzing: false,
      analysisError: '',
      analysisSuccess: false,
      aiApiUrl: 'https://bjd-api.seedvault.cn/api/smart-device/plan',
      aiStreamApiUrl: 'https://bjd-api.seedvault.cn/api/smart-device/plan-stream',
      // aiApiUrl: 'http://127.0.0.1:8080/api/smart-device/plan',
      // aiStreamApiUrl: 'http://127.0.0.1:8080/api/smart-device/plan-stream',
      aiPhase: '',
      thinkingContent: '',
      generatingContent: '',
      isThinkingExpanded: false,
      
      // Drag offset for smooth dragging
      dragOffset: null,
      
      // Summary bottom bar related
      isSummaryExpanded: false,
      
      // Device colors by type
      deviceColors: {
        smart_lock: '#dc3545',
        camera: '#ffc107',
        motion_sensor: '#28a745',
        smart_switch: '#17a2b8',
        dimmer: '#6f42c1',
        smart_curtain: '#fd7e14',
        temp_humidity_sensor: '#e83e8c',
        air_quality_sensor: '#20c997',
        smart_speaker: '#6c757d',
        ac_controller: '#007bff',
        ceiling_ap: '#343a40',
        wall_ap: '#6c757d',
        gas_alarm: '#dc3545',
        water_leak_alarm: '#007bff',
        smart_toilet: '#e83e8c',
        smart_control_panel: '#6f42c1',
        ac_gateway: '#343a40',
        panel_ap: '#6c757d'
      },
      
      // Category labels - only show specified categories
      categoryLabels: {
        security_systems: '安防系统',
        lighting_control: '照明控制',
        environment_control: '环境控制',
        bathroom_smart: '智能卫浴',
        interaction_hub: '交互中心',
        network_infrastructure: '网络设施'
      },
      
      // Device type labels for display
      deviceTypeLabels: {
        smart_lock: '智能门锁',
        camera: '摄像头',
        motion_sensor: '人体传感器',
        smart_switch: '智能开关',
        dimmer: '调光器',
        smart_curtain: '智能窗帘',
        temp_humidity_sensor: '温湿度传感器',
        air_quality_sensor: '空气质量传感器',
        smart_speaker: '智能音箱',
        ac_controller: '空调控制器',
        ceiling_ap: '吸顶式AP',
        wall_ap: '墙面AP',
        gas_alarm: '燃气报警器',
        water_leak_alarm: '水浸报警器',
        smart_toilet: '智能马桶',
        smart_control_panel: '智能中控屏',
        ac_gateway: 'AC一体化网关',
        panel_ap: '面板式AP'
      },
      
      // Map device types to categories
      deviceTypeToCategory: {
        smart_lock: 'security_systems',
        camera: 'security_systems',
        gas_alarm: 'security_systems',
        water_leak_alarm: 'security_systems',
        
        smart_switch: 'lighting_control',
        smart_curtain: 'lighting_control',
        
        temp_humidity_sensor: 'environment_control',
        air_quality_sensor: 'environment_control',
        
        smart_toilet: 'bathroom_smart',
        
        smart_control_panel: 'interaction_hub',
        smart_speaker: 'interaction_hub',
        
        ac_gateway: 'network_infrastructure',
        ceiling_ap: 'network_infrastructure',
        panel_ap: 'network_infrastructure'
      },
      
      // Selected categories for filtering
      selectedCategories: {},
      
      // Product database
      productDatabase: {
        smart_lock: [
          { id: 'lock-1', brand: '樱花', model: 'DZ-1022-一握开锁', price: 599 },
          { id: 'lock-2', brand: '樱花', model: 'DZ-H300V-智能猫眼锁', price: 699 },
          { id: 'lock-3', brand: '樱花', model: 'DZ-F5-人脸识别锁', price: 1099 }
        ],
        camera: [
          { id: 'camera-1', brand: '海康威视', model: '萤石C6CN', price: 299 },
          { id: 'camera-2', brand: '小米', model: '智能摄像机2K Pro', price: 349 },
          { id: 'camera-3', brand: '华为', model: '智能摄像机Pro 2K', price: 399 },
          { id: 'camera-4', brand: '360', model: '智能摄像机云台7P', price: 249 }
        ],
        gas_alarm: [
          { id: 'gas-1', brand: '小米', model: '燃气报警器', price: 129 },
          { id: 'gas-2', brand: '华为', model: '燃气传感器', price: 159 },
          { id: 'gas-3', brand: '海康威视', model: '萤石T9', price: 199 },
          { id: 'gas-4', brand: '霍尼韦尔', model: '燃气报警器', price: 249 }
        ],
        water_leak_alarm: [
          { id: 'water-1', brand: '小米', model: '水浸传感器', price: 79 },
          { id: 'water-2', brand: '华为', model: '水浸传感器', price: 99 },
          { id: 'water-3', brand: '海康威视', model: '萤石T5', price: 129 },
          { id: 'water-4', brand: '绿米', model: '水浸传感器', price: 89 }
        ],
        smart_switch: [
          { id: 'switch-1', brand: '小米', model: '智能开关单开', price: 59 },
          { id: 'switch-2', brand: '华为', model: '智能开关单开', price: 69 },
          { id: 'switch-3', brand: '绿米', model: '智能墙壁开关', price: 79 },
          { id: 'switch-4', brand: '欧瑞博', model: '智能开关单开', price: 89 }
        ],
        smart_curtain: [
          { id: 'curtain-1', brand: '小米', model: '智能窗帘电机', price: 499 },
          { id: 'curtain-2', brand: '华为', model: '智能窗帘电机', price: 599 },
          { id: 'curtain-3', brand: '绿米', model: '智能窗帘电机', price: 549 },
          { id: 'curtain-4', brand: '杜亚', model: '智能窗帘电机', price: 399 }
        ],
        smart_toilet: [
          { id: 'toilet-1', brand: '小米', model: '智能马桶Pro', price: 2999 },
          { id: 'toilet-2', brand: '华为', model: '智能马桶S3', price: 3599 },
          { id: 'toilet-3', brand: '九牧', model: '智能马桶Z1S', price: 4999 },
          { id: 'toilet-4', brand: '恒洁', model: '智能马桶Q9', price: 3999 }
        ],
        temp_humidity_sensor: [
          { id: 'temp-1', brand: '小米', model: '温湿度传感器2', price: 49 },
          { id: 'temp-2', brand: '华为', model: '温湿度传感器', price: 59 },
          { id: 'temp-3', brand: '绿米', model: '温湿度传感器', price: 49 },
          { id: 'temp-4', brand: '霍尼韦尔', model: '温湿度传感器', price: 99 }
        ],
        smart_control_panel: [
          { id: 'panel-1', brand: '小米', model: '智能中控屏Pro', price: 899 },
          { id: 'panel-2', brand: '华为', model: '智能中控屏S3', price: 1299 },
          { id: 'panel-3', brand: '欧瑞博', model: '智能中控屏MixPad X', price: 1699 },
          { id: 'panel-4', brand: '绿米', model: '智能中控屏', price: 799 }
        ],
        smart_speaker: [
          { id: 'speaker-1', brand: '小米', model: '小爱同学Pro', price: 299 },
          { id: 'speaker-2', brand: '华为', model: '小艺音箱', price: 199 },
          { id: 'speaker-3', brand: '天猫精灵', model: 'X1', price: 99 },
          { id: 'speaker-4', brand: '百度', model: '小度音箱Pro', price: 169 }
        ],
        ac_gateway: [
          { id: 'ac-1', brand: 'TP-Link', model: 'AC一体化网关', price: 399 },
          { id: 'ac-2', brand: '华为', model: 'AC网关', price: 599 },
          { id: 'ac-3', brand: '锐捷', model: 'AC网关', price: 499 },
          { id: 'ac-4', brand: '华三', model: 'AC网关', price: 699 }
        ],
        ceiling_ap: [
          { id: 'ceiling-ap-1', brand: 'TP-Link', model: '吸顶式AP', price: 499 },
          { id: 'ceiling-ap-2', brand: '华为', model: '吸顶式AP', price: 899 },
          { id: 'ceiling-ap-3', brand: '锐捷', model: '吸顶式AP', price: 699 },
          { id: 'ceiling-ap-4', brand: '华三', model: '吸顶式AP', price: 799 }
        ],
        panel_ap: [
          { id: 'panel-ap-1', brand: 'TP-Link', model: '面板式AP', price: 299 },
          { id: 'panel-ap-2', brand: '华为', model: '面板式AP', price: 499 },
          { id: 'panel-ap-3', brand: '锐捷', model: '面板式AP', price: 399 },
          { id: 'panel-ap-4', brand: '华三', model: '面板式AP', price: 599 }
        ]
      },
      
      selectedProducts: {},
      resizeObserver: null
    };
  },
  computed: {
    thinkingContentHtml() {
      return marked.parse(this.thinkingContent || '');
    },
    generatingContentHtml() {
      return marked.parse(this.generatingContent || '');
    },

    // Get all categories from categoryLabels
    allCategories() {
      return Object.keys(this.categoryLabels).sort();
    },
    
    // Check if all categories are selected
    isAllSelected() {
      return this.allCategories.length > 0 && this.allCategories.every(category => this.selectedCategories[category]);
    },
    
    // Check if some but not all categories are selected (indeterminate state)
    isIndeterminate() {
      const selectedCount = this.allCategories.filter(category => this.selectedCategories[category]).length;
      return selectedCount > 0 && selectedCount < this.allCategories.length;
    },
    
    // Devices filtered by selected categories
    filteredDevices() {
      const allDevices = Object.values(this.categorizedDevices).flat();
      
      // If all categories selected, show all devices
      if (this.isAllSelected) {
        return allDevices;
      }
      
      // If no categories selected, show no devices
      const hasSelectedCategories = Object.values(this.selectedCategories).some(Boolean);
      if (!hasSelectedCategories) {
        return [];
      }
      
      // Filter devices by selected categories
      return allDevices.filter(device => {
        const category = this.deviceTypeToCategory[device.type];
        return this.selectedCategories[category];
      });
    },
    
    allDevices() {
      const devices = this.filteredDevices;
      return devices.sort((a, b) => {
        if (a === this.selectedItem) return 1;
        if (b === this.selectedItem) return -1;
        return a.name.localeCompare(b.name);
      });
    },
    
    // Devices that should be visible based on product selection
    visibleDevices() {
      return this.allDevices.filter(device => {
        // Check product selection filter
        if (this.showOnlyDevicesWithProducts) {
          // Only show devices that have a product selected
          return this.getSelectedProduct(device) !== null;
        }
        
        return true;
      });
    },
    selectedDevicesSummary() {
      const summary = [];
      Object.values(this.categorizedDevices).forEach(categoryDevices => {
        categoryDevices.forEach(device => {
          const product = this.getSelectedProduct(device);
          if (product) {
            summary.push({ device, product });
          }
        });
      });
      return summary;
    },
    totalPrice() {
      return this.selectedDevicesSummary.reduce((total, item) => {
        return total + item.product.price;
      }, 0);
    }
  },
  mounted() {
    this.loadBackgroundImage();
    this.useMockData();
    this.$nextTick(() => {
      this.initCategorySelections();
      this.updateStageSize();
      this.setupResizeObserver();
    });
    this.handleResponsiveLayout();
    window.addEventListener('resize', this.handleResponsiveLayout);
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    window.removeEventListener('resize', this.handleResponsiveLayout);
  },
  methods: {
    updateStageSize() {
      const container = this.$el.querySelector('.floor-plan-content');
      if (container) {
        this.stageConfig.width = container.clientWidth;
        this.stageConfig.height = container.clientHeight;
      }
    },

    setupResizeObserver() {
      const container = this.$el.querySelector('.floor-plan-content');
      if (!container) return;
      this.resizeObserver = new ResizeObserver(() => {
        this.updateStageSize();
      });
      this.resizeObserver.observe(container);
    },

    handleResponsiveLayout() {
      if (window.innerWidth < 1024) {
        this.isLeftPanelOpen = false;
        this.isRightPanelOpen = false;
      }
    },

    calculateImageFit() {
      if (!this.originalImageSize.width || !this.stageConfig.width) return;

      const containerW = this.stageConfig.width;
      const containerH = this.stageConfig.height;
      const imgW = this.originalImageSize.width;
      const imgH = this.originalImageSize.height;

      // 计算适配比例（contain 模式）
      const scaleX = containerW / imgW;
      const scaleY = containerH / imgH;
      const fitScale = Math.min(scaleX, scaleY);

      // 图片显示尺寸
      this.imageConfig.width = imgW * fitScale;
      this.imageConfig.height = imgH * fitScale;

      // 居中偏移
      this.imageConfig.x = (containerW - this.imageConfig.width) / 2;
      this.imageConfig.y = (containerH - this.imageConfig.height) / 2;

      // 重置缩放和位置
      this.scale = 1;
      this.stagePosition = { x: 0, y: 0 };
    },

    handleWheel(e) {
      e.evt.preventDefault();

      const oldScale = this.scale;
      const scaleBy = 1.1;

      // 滚轮方向决定放大/缩小
      const newScale = e.evt.deltaY < 0
        ? Math.min(oldScale * scaleBy, this.maxScale)
        : Math.max(oldScale / scaleBy, this.minScale);

      // 以鼠标位置为中心缩放
      const stage = this.$refs.stageRef.getStage();
      const pointer = stage.getPointerPosition();

      const mousePointTo = {
        x: (pointer.x - this.stagePosition.x) / oldScale,
        y: (pointer.y - this.stagePosition.y) / oldScale
      };

      this.scale = newScale;
      this.stagePosition = {
        x: pointer.x - mousePointTo.x * newScale,
        y: pointer.y - mousePointTo.y * newScale
      };
    },

    zoomIn() {
      const oldScale = this.scale;
      const newScale = Math.min(oldScale * 1.2, this.maxScale);
      // 以画布中心为缩放中心
      const centerX = this.stageConfig.width / 2;
      const centerY = this.stageConfig.height / 2;
      const mousePointTo = {
        x: (centerX - this.stagePosition.x) / oldScale,
        y: (centerY - this.stagePosition.y) / oldScale
      };
      this.scale = newScale;
      this.stagePosition = {
        x: centerX - mousePointTo.x * newScale,
        y: centerY - mousePointTo.y * newScale
      };
    },

    zoomOut() {
      const oldScale = this.scale;
      const newScale = Math.max(oldScale / 1.2, this.minScale);
      const centerX = this.stageConfig.width / 2;
      const centerY = this.stageConfig.height / 2;
      const mousePointTo = {
        x: (centerX - this.stagePosition.x) / oldScale,
        y: (centerY - this.stagePosition.y) / oldScale
      };
      this.scale = newScale;
      this.stagePosition = {
        x: centerX - mousePointTo.x * newScale,
        y: centerY - mousePointTo.y * newScale
      };
    },

    resetView() {
      this.calculateImageFit();
    },

    loadBackgroundImage() {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = this.backgroundImageUrl;
      img.onload = () => {
        this.backgroundImage = img;
        this.originalImageSize = { width: img.width, height: img.height };
        this.calculateImageFit();
      };
      img.onerror = () => {
        console.warn('Failed to load default background image');
      };
    },
    
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.selectedFile = file;
        this.uploadError = '';
        this.uploadSuccess = false;
        // Reset AI analysis status when new file is selected
        this.analysisSuccess = false;
        this.analysisError = '';
        this.thinkingContent = '';
        this.generatingContent = '';
        this.aiPhase = '';
        // Automatically upload to img host when file is selected
        this.uploadToImgHost();
      }
    },
    
    // 重写的上传方法
    async uploadToImgHost() {
      if (!this.selectedFile) {
        this.uploadError = '请先选择图片文件';
        return;
      }
      
      try {
        this.isUploading = true;
        this.uploadError = '';
        this.uploadSuccess = false;
        
        const formData = new FormData();
        // ImgBB 要求的参数
        formData.append('key', this.imgHostApiKey);
        formData.append('image', this.selectedFile); // 字段名必须是 'image'
        
        console.log('正在上传到 ImgBB...');
        
        // 发起请求
        const response = await fetch(this.imgHostApiUrl, {
          method: 'POST',
          body: formData
          // 不需要手动设置 headers，浏览器会自动设置 multipart/form-data
          // ImgBB 支持 CORS，所以浏览器不会拦截
        });
        
        const result = await response.json();
        console.log('ImgBB 响应:', result);
        
        if (result.success && result.data && result.data.url) {
          // 上传成功
          this.backgroundImageUrl = result.data.url;
          this.loadBackgroundImage();
          // Clear all device-related information when new image is uploaded
          this.categorizedDevices = {}; // Clear device candidate list (设备候选列表)
          this.selectedProducts = {}; // Clear selected products for all devices
          this.selectedItem = null; // Clear selected device
          this.analysisSuccess = false; // Reset analysis status
          this.analysisError = ''; // Clear any previous analysis error
          this.uploadSuccess = true;
        } else {
          // API 返回了错误
          this.uploadError = `上传失败：${result.error ? result.error.message : '未知错误'}`;
        }
      } catch (error) {
        console.error('Upload error:', error);
        this.uploadError = `上传出错：${error.message}`;
      } finally {
        this.isUploading = false;
      }
    },
    
    // Perform AI analysis (SSE streaming)
    async performAiAnalysis() {
      if (!this.backgroundImageUrl) {
        this.analysisError = '请先上传图片';
        return;
      }

      try {
        this.isAnalyzing = true;
        this.analysisError = '';
        this.analysisSuccess = false;
        this.aiPhase = '';
        this.thinkingContent = '';
        this.generatingContent = '';
        this.isThinkingExpanded = true;

        const requestBody = { imageUrl: this.backgroundImageUrl };

        const response = await fetch(this.aiStreamApiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream'
          },
          body: JSON.stringify(requestBody),
          mode: 'cors',
          credentials: 'omit',
          redirect: 'follow'
        });

        if (!response.ok) {
          let errorMessage = `API请求失败，状态码：${response.status}`;
          try {
            const errorData = await response.text();
            if (errorData) errorMessage += ` - ${errorData}`;
          } catch (e) { /* ignore parse error */ }
          throw new Error(errorMessage);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        let gotResult = false;

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const messages = buffer.split('\n\n');
          buffer = messages.pop() || '';

          for (const message of messages) {
            if (!message.trim()) continue;

            let eventName = '';
            let dataLines = [];

            for (const line of message.split('\n')) {
              if (line.startsWith('event:')) {
                eventName = line.substring(6).trim();
              } else if (line.startsWith('data:')) {
                dataLines.push(line.substring(5).trim());
              }
            }
            const data = dataLines.join('\n');

            switch (eventName) {
              case 'thinking':
                this.aiPhase = 'thinking';
                this.thinkingContent += data;
                this.scrollToBottom('thinkingScroll');
                break;

              case 'content':
                this.aiPhase = 'generating';
                this.generatingContent += data;
                this.scrollToBottom('generatingScroll');
                break;

              case 'result':
                gotResult = true;
                try {
                  const aiResponse = JSON.parse(data);
                  if (aiResponse.categorized_devices) {
                    this.categorizedDevices = aiResponse.categorized_devices;
                    this.selectDefaultProducts();
                    if (aiResponse.room_analysis) {
                      this.roomAnalysis = aiResponse.room_analysis;
                    }
                    this.analysisSuccess = true;
                  } else {
                    this.analysisError = 'AI分析失败：未返回有效数据';
                  }
                } catch (parseError) {
                  this.analysisError = 'AI分析结果解析失败';
                }
                break;

              case 'error':
                this.analysisError = `AI分析出错：${data}`;
                break;
            }
          }
        }

        // Process remaining buffer
        if (buffer.trim()) {
          let eventName = '';
          let dataLines = [];
          for (const line of buffer.split('\n')) {
            if (line.startsWith('event:')) {
              eventName = line.substring(6).trim();
            } else if (line.startsWith('data:')) {
              dataLines.push(line.substring(5).trim());
            }
          }
          const data = dataLines.join('\n');
          if (eventName === 'result' && data) {
            gotResult = true;
            try {
              const aiResponse = JSON.parse(data);
              if (aiResponse.categorized_devices) {
                this.categorizedDevices = aiResponse.categorized_devices;
                this.selectDefaultProducts();
                if (aiResponse.room_analysis) {
                  this.roomAnalysis = aiResponse.room_analysis;
                }
                this.analysisSuccess = true;
              }
            } catch (e) { /* ignore parse error for fallback */ }
          }
        }

        // Fallback: if stream ended without result event, try parsing accumulated content
        if (!gotResult && this.generatingContent) {
          try {
            const aiResponse = JSON.parse(this.generatingContent);
            if (aiResponse.categorized_devices) {
              this.categorizedDevices = aiResponse.categorized_devices;
              this.selectDefaultProducts();
              if (aiResponse.room_analysis) {
                this.roomAnalysis = aiResponse.room_analysis;
              }
              this.analysisSuccess = true;
            }
          } catch (e) {
            this.analysisError = 'AI分析结果解析失败';
          }
        }

      } catch (error) {
        if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
          this.analysisError = `AI分析出错：无法连接到API服务器。请检查API服务是否正在运行。`;
        } else {
          this.analysisError = `AI分析出错：${error.message}`;
        }
      } finally {
        this.isAnalyzing = false;
        this.aiPhase = '';
      }
    },

    toggleThinkingPanel() {
      this.isThinkingExpanded = !this.isThinkingExpanded;
    },

    scrollToBottom(refName) {
      this.$nextTick(() => {
        const el = this.$refs[refName];
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    useMockData() {
      // 保持原有的 mock 数据不变
      const mockData = {
        "room_analysis": [
          { "room_name": "客厅 (Living Room)", "function": "家庭核心活动区", "key_needs": ["全屋网络核心覆盖", "智能灯光场景控制"] },
          { "room_name": "卧室A (Bedroom A)", "function": "次卧/客房", "key_needs": ["舒适睡眠灯光", "窗帘自动化"] },
          { "room_name": "卧室B (Bedroom B)", "function": "主卧", "key_needs": ["高品质睡眠环境", "起夜灯光模式"] },
          { "room_name": "卧室C (Bedroom C)", "function": "儿童房/书房", "key_needs": ["基础照明控制", "窗帘控制"] },
          { "room_name": "厨房 (Kitchen)", "function": "烹饪区域", "key_needs": ["燃气安全监测", "照明控制"] },
          { "room_name": "卫生间A & B (Bathrooms)", "function": "洗漱与如厕区域", "key_needs": ["漏水监测", "智能马桶"] }
        ],
        "categorized_devices": {
          "security_systems": [
            { "type": "smart_lock", "name": "智能门锁", "reason": "位于入户门内侧", "position": { "x": 0.64, "y": 0.43 } },
            { "type": "gas_alarm", "name": "燃气报警器", "reason": "位于厨房吊顶中心偏左", "position": { "x": 0.48, "y": 0.19 } },
            { "type": "water_leak_alarm", "name": "水浸报警器 (卫A)", "reason": "位于卫生间A洗手台下方", "position": { "x": 0.36, "y": 0.38 } },
            { "type": "water_leak_alarm", "name": "水浸报警器 (卫B)", "reason": "位于卫生间B洗手台下方", "position": { "x": 0.58, "y": 0.21 } }
          ],
          "lighting_control": [
            { "type": "smart_switch", "name": "智能开关 (客厅)", "reason": "位于客厅入户侧墙面", "position": { "x": 0.62, "y": 0.46 } },
            { "type": "smart_switch", "name": "智能开关 (卧室B)", "reason": "位于卧室B进门右手边", "position": { "x": 0.42, "y": 0.51 } },
            { "type": "smart_switch", "name": "智能开关 (卧室A)", "reason": "位于卧室A进门左手边", "position": { "x": 0.58, "y": 0.51 } },
            { "type": "smart_switch", "name": "智能开关 (卧室C)", "reason": "位于卧室C进门右手边", "position": { "x": 0.45, "y": 0.28 } },
            { "type": "smart_curtain", "name": "智能窗帘 (阳台)", "reason": "安装于客厅通往阳台的推拉门", "position": { "x": 0.50, "y": 0.76 } },
            { "type": "smart_curtain", "name": "智能窗帘 (卧室B)", "reason": "安装于卧室B飘窗", "position": { "x": 0.35, "y": 0.73 } },
            { "type": "smart_curtain", "name": "智能窗帘 (卧室A)", "reason": "安装于卧室A飘窗", "position": { "x": 0.65, "y": 0.73 } },
            { "type": "smart_curtain", "name": "智能窗帘 (卧室C)", "reason": "安装于卧室C北侧窗户", "position": { "x": 0.40, "y": 0.16 } }
          ],
          "bathroom_smart": [
            { "type": "smart_toilet", "name": "智能马桶 (卫A)", "reason": "位于卫生间A左侧", "position": { "x": 0.32, "y": 0.38 } },
            { "type": "smart_toilet", "name": "智能马桶 (卫B)", "reason": "位于卫生间B右侧", "position": { "x": 0.63, "y": 0.21 } }
          ],
          "environment_control": [
            { "type": "temp_humidity_sensor", "name": "温湿度传感器 (客厅)", "reason": "位于客厅沙发旁", "position": { "x": 0.55, "y": 0.60 } },
            { "type": "temp_humidity_sensor", "name": "温湿度传感器 (卧室B)", "reason": "位于卧室B床头", "position": { "x": 0.35, "y": 0.60 } },
            { "type": "temp_humidity_sensor", "name": "温湿度传感器 (卧室A)", "reason": "位于卧室A床头柜", "position": { "x": 0.65, "y": 0.60 } }
          ],
          "interaction_hub": [
            { "type": "smart_control_panel", "name": "智能中控屏", "reason": "位于入户玄关右手边", "position": { "x": 0.63, "y": 0.45 } },
            { "type": "smart_speaker", "name": "智能音箱", "reason": "位于客厅电视柜旁", "position": { "x": 0.52, "y": 0.58 } }
          ],
          "network_infrastructure": [
            { "type": "ac_gateway", "name": "AC一体化网关", "reason": "位于入户玄关左侧", "position": { "x": 0.62, "y": 0.41 } },
            { "type": "ceiling_ap", "name": "吸顶式AP", "reason": "位于客厅天花板中心", "position": { "x": 0.50, "y": 0.48 } },
            { "type": "panel_ap", "name": "面板式AP", "reason": "位于卧室B进门侧", "position": { "x": 0.42, "y": 0.56 } }
          ]
        }
      };
      this.categorizedDevices = mockData.categorized_devices;
      this.selectDefaultProducts();
      // Initialize category selections after loading mock data
      this.$nextTick(() => {
        this.initCategorySelections();
      });
    },
    
    selectDevice(device) {
      this.selectedItem = device;
      this.$nextTick(() => {
        const deviceItem = document.querySelector(`.device-item.selected`);
        if (deviceItem) {
          const controlsPanel = deviceItem.closest('.right-controls');
          if (controlsPanel) {
            const itemTop = deviceItem.offsetTop;
            const itemHeight = deviceItem.offsetHeight;
            const panelHeight = controlsPanel.offsetHeight;
            const scrollTop = itemTop - (panelHeight / 2) + (itemHeight / 2);
            controlsPanel.scrollTo({
              top: Math.max(0, scrollTop),
              behavior: 'smooth'
            });
          }
        }
      });
    },
    
    handleStageClick(e) {
      if (this.newDeviceType) {
        const stage = this.$refs.stageRef.getStage();
        const pos = stage.getPointerPosition();
        this.addDeviceAtPosition(pos.x, pos.y);
      } else {
        if (e.target === e.currentTarget) {
          this.selectedItem = null;
        }
      }
    },

    // Add a new device at the given stage coordinates
    addDeviceAtPosition(x, y) {
      // 转换为图片坐标系
      const imageX = (x - this.stagePosition.x) / this.scale - this.imageConfig.x;
      const imageY = (y - this.stagePosition.y) / this.scale - this.imageConfig.y;

      // 归一化坐标
      const normalizedX = Math.max(0, Math.min(1, imageX / this.imageConfig.width));
      const normalizedY = Math.max(0, Math.min(1, imageY / this.imageConfig.height));

      // Determine device category
      const category = this.deviceTypeToCategory[this.newDeviceType];
      if (!category) return;

      // Create new device
      const newDevice = {
        type: this.newDeviceType,
        name: `${this.deviceTypeLabels[this.newDeviceType]} ${Date.now().toString().slice(-4)}`,
        reason: '手动添加',
        position: {
          x: normalizedX,
          y: normalizedY
        }
      };

      // Add to categorized devices
      if (!this.categorizedDevices[category]) {
        this.categorizedDevices[category] = [];
      }
      this.categorizedDevices[category].push(newDevice);
      this.categorizedDevices = { ...this.categorizedDevices };

      // Select the new device
      this.selectDevice(newDevice);

      // Reset new device type
      this.newDeviceType = '';
    },
    
    // Cancel adding a new device
    cancelAddDevice() {
      this.newDeviceType = '';
    },
    
    // Toggle left panel visibility
    toggleLeftPanel() {
      this.isLeftPanelOpen = !this.isLeftPanelOpen;
    },
    
    // Toggle right panel visibility
    toggleRightPanel() {
      this.isRightPanelOpen = !this.isRightPanelOpen;
    },
    

    
    getSelectedProduct(device) {
      const productId = this.selectedProducts[device.name];
      if (!productId) return null;
      const products = this.productDatabase[device.type] || [];
      return products.find(product => product.id === productId);
    },
    
    handleProductChange(device, event) {
      const productId = event.target.value;
      console.log(`Selected product ${productId} for device ${device.name}`);
    },

    selectDefaultProducts() {
      for (const category of Object.values(this.categorizedDevices)) {
        if (!Array.isArray(category)) continue;
        for (const device of category) {
          if (this.selectedProducts[device.name]) continue;
          const products = this.productDatabase[device.type] || [];
          if (products.length > 0) {
            this.selectedProducts[device.name] = products[0].id;
          }
        }
      }
    },
    
    // Handle category selection change
    handleCategoryChange(category, event) {
      this.selectedCategories[category] = event.target.checked;
    },
    
    // Group devices by their type
    groupDevicesByType(devices) {
      const grouped = {};
      devices.forEach(device => {
        if (!grouped[device.type]) {
          grouped[device.type] = [];
        }
        grouped[device.type].push(device);
      });
      return grouped;
    },
    
    // Handle select all toggle
    handleSelectAllChange() {
      // When in all-selected state, click to deselect all
      // When in indeterminate or all-deselected state, click to select all
      const shouldSelectAll = !this.isAllSelected;
      
      // Update all individual category selections
      this.allCategories.forEach(category => {
        this.selectedCategories[category] = shouldSelectAll;
      });
    },
    
    // Initialize category selections when categorizedDevices changes
    initCategorySelections() {
      this.allCategories.forEach(category => {
        // If no selections yet, select all by default
        this.selectedCategories[category] = true;
      });
    },
    
    // Drag event handlers for devices
    handleDragStart(e) {
      e.cancelBubble = true;
      const stage = this.$refs.stageRef.getStage();
      const pointerPos = stage.getPointerPosition();
      this.dragOffset = {
        x: pointerPos.x,
        y: pointerPos.y
      };
    },

    handleDragMove(e, device) {
      const stage = this.$refs.stageRef.getStage();
      const pointerPos = stage.getPointerPosition();

      // 计算增量（像素），考虑缩放
      const deltaX = (pointerPos.x - this.dragOffset.x) / this.scale;
      const deltaY = (pointerPos.y - this.dragOffset.y) / this.scale;

      // 转换为归一化增量
      const normalizedDeltaX = deltaX / this.imageConfig.width;
      const normalizedDeltaY = deltaY / this.imageConfig.height;

      // 增量更新位置
      device.position = {
        x: Math.max(0, Math.min(1, device.position.x + normalizedDeltaX)),
        y: Math.max(0, Math.min(1, device.position.y + normalizedDeltaY))
      };

      this.dragOffset = {
        x: pointerPos.x,
        y: pointerPos.y
      };
    },

    handleDragEnd() {
      this.dragOffset = null;
    },

    handleStageDragEnd() {
      const stage = this.$refs.stageRef.getStage();
      this.stagePosition = {
        x: stage.x(),
        y: stage.y()
      };
    },
    
    // Calculate text width considering Chinese characters
    calculateTextWidth(text, fontSize) {
      // Chinese characters are generally wider than English letters/numbers/symbols
      // We'll use a ratio to adjust for this
      const chineseRegex = /[\u4e00-\u9fa5]/g;
      const chineseChars = text.match(chineseRegex) || [];
      const chineseCount = chineseChars.length;
      const otherCount = text.length - chineseCount;
      
      // Base width per character (adjust based on font size)
      const baseWidth = fontSize * 0.6;
      const chineseWidth = fontSize * 0.9;
      
      // Calculate total width
      return (chineseCount * chineseWidth) + (otherCount * baseWidth);
    },
    
    // Export order to PDF
    async exportToPdf() {
      try {
        // Dynamically import libraries
        const [jsPDF, html2canvas] = await Promise.all([
          import('jspdf'),
          import('html2canvas')
        ]);
        
        // Create a hidden container for the order content
        const container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.left = '-9999px';
        container.style.width = '800px';
        container.style.padding = '20px';
        container.style.backgroundColor = '#fff';
        container.style.fontFamily = 'Arial, sans-serif';
        document.body.appendChild(container);
        
        // Generate order HTML
        container.innerHTML = `
          <div style="width: 100%; max-width: 800px; margin: 0 auto;">
            <h1 style="text-align: center; font-size: 24px; margin-bottom: 20px;">设备订单汇总</h1>
            <p style="font-size: 14px; margin-bottom: 30px;">生成时间: ${new Date().toLocaleString()}</p>
            
            <h2 style="font-size: 18px; margin-bottom: 20px;">设备列表:</h2>
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
              <thead>
                <tr style="background-color: #f5f5f5;">
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">序号</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">设备名称</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">产品详情</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">价格</th>
                </tr>
              </thead>
              <tbody>
                ${this.selectedDevicesSummary.map((item, index) => `
                  <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;">${index + 1}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${item.device.name}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${item.product.brand} ${item.product.model}</td>
                    <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">¥${item.product.price}</td>
                  </tr>
                `).join('')}
              </tbody>
              <tfoot>
                <tr style="background-color: #f5f5f5; font-weight: bold;">
                  <td colspan="3" style="border: 1px solid #ddd; padding: 8px; text-align: right;">总计:</td>
                  <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">¥${this.totalPrice}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        `;
        
        // Wait for any images to load
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Convert HTML to canvas
        const canvas = await html2canvas.default(container, {
          scale: 2, // Higher scale for better quality
          useCORS: true,
          logging: false
        });
        
        // Remove the temporary container
        document.body.removeChild(container);
        
        // Create PDF from canvas
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF.default({
          orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
          unit: 'mm',
          format: 'a4'
        });
        
        // Calculate dimensions for A4 (210mm x 297mm)
        const imgWidth = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        // Add image to PDF
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        
        // Save the PDF
        pdf.save(`设备订单_${new Date().getTime()}.pdf`);
      } catch (error) {
        console.error('PDF Export Error:', error);
        this.analysisError = `导出PDF失败：${error.message}`;
      }
    },
    
    // Delete a device
    deleteDevice(device) {
      // Find the category this device belongs to
      const category = this.deviceTypeToCategory[device.type];
      if (!category) return;
      
      // Find the device in its category array
      const categoryDevices = this.categorizedDevices[category] || [];
      const deviceIndex = categoryDevices.findIndex(d => d.name === device.name);
      
      if (deviceIndex > -1) {
        // Remove the device from the category
        this.categorizedDevices[category].splice(deviceIndex, 1);
        
        // If this was the selected device, clear selection
        if (this.selectedItem === device) {
          this.selectedItem = null;
        }
        
        // Remove product selection for this device
        if (this.selectedProducts[device.name]) {
          const products = { ...this.selectedProducts };
          delete products[device.name];
          this.selectedProducts = products;
        }

        this.categorizedDevices = { ...this.categorizedDevices };
      }
    }
  }
};
</script>

<style scoped>
.floor-plan-editor {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background-color: #f5f5f5;
}

.main-content-row {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.panel-header h3,
.panel-header h4 {
  margin: 0;
}

.toggle-panel-btn {
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
}

.toggle-panel-btn:hover {
  background-color: #3aa875;
}

/* Left panel toggle button - arrow points left when open, right when collapsed */
.left-controls .toggle-panel-btn::before {
  content: '◀';
}

.left-controls.collapsed .toggle-panel-btn::before {
  content: '▶';
}

/* Right panel toggle button - arrow points right when open, left when collapsed */
.right-controls .toggle-panel-btn::before {
  content: '▶';
}

.right-controls.collapsed .toggle-panel-btn::before {
  content: '◀';
}

.left-controls {
  width: 280px;
  padding: 20px;
  background-color: #fff;
  border-right: 1px solid #ddd;
  overflow-y: auto;
  box-shadow: 2px 0 10px rgba(0,0,0,0.05);
  transition: width 0.3s ease;
}

.left-controls.collapsed {
  width: 50px;
  padding: 10px;
  overflow: hidden;
}

.left-controls.collapsed .control-section,
.left-controls.collapsed h3 {
  display: none;
}

.right-controls {
  width: 350px;
  padding: 20px;
  background-color: #fff;
  border-left: 1px solid #ddd;
  overflow-y: auto;
  box-shadow: -2px 0 10px rgba(0,0,0,0.05);
  transition: width 0.3s ease;
}

.right-controls.collapsed {
  width: 50px;
  padding: 10px;
  overflow: hidden;
}

.right-controls.collapsed .section,
.right-controls.collapsed h4 {
  display: none;
}

/* Ensure toggle button is still visible when collapsed */
.left-controls.collapsed .toggle-panel-btn,
.right-controls.collapsed .toggle-panel-btn {
  display: block;
  margin: 0 auto;
}

.left-controls.collapsed .panel-header,
.right-controls.collapsed .panel-header {
  display: flex;
  justify-content: center;
  margin-bottom: 5px;
}

.floor-plan-content {
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  justify-content: center;
  align-items: center;
  background-color: #e9ecef;
}

/* Bottom summary bar styles */
.summary-bottom-bar {
  background-color: #fff;
  border-top: 1px solid #ddd;
  box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
  z-index: 100;
  position: relative;
  transition: all 0.3s ease;
  max-height: 60px;
  overflow: hidden;
  cursor: pointer;
  flex-shrink: 0;
}

.summary-bottom-bar.expanded {
  max-height: 300px;
}

.summary-bottom-bar .summary-content {
  padding: 15px 20px;
}

.summary-bottom-bar .summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 5px;
  border-bottom: 1px solid #e9ecef;
}

.summary-bottom-bar h4 {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.summary-bottom-bar .summary-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Main row container */
.summary-bottom-bar .summary-items-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  width: 100%;
}

/* Left side - device list */
.summary-bottom-bar .summary-list-container {
  flex: 1;
  min-width: 0;
  max-height: 200px;
  overflow-y: auto;
}

.summary-bottom-bar .summary-list {
  background-color: #f8f9fa;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #e9ecef;
  max-height: 200px;
  overflow-y: auto;
}

.summary-bottom-bar .summary-item {
  padding: 6px 10px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
}

.summary-bottom-bar .summary-item:last-child {
  border-bottom: none;
}

.summary-bottom-bar .summary-device-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.summary-bottom-bar .summary-device-name {
  font-weight: 600;
  color: #333;
  display: block;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.summary-bottom-bar .summary-product-name {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.summary-bottom-bar .summary-price {
  font-weight: 600;
  color: #28a745;
  font-size: 14px;
  white-space: nowrap;
}

/* Right side - total price */
.summary-bottom-bar .summary-total-container {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  gap: 10px;
  white-space: nowrap;
}

/* Export PDF button */
.export-pdf-btn {
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
  align-self: flex-end;
}

.export-pdf-btn:hover {
  background-color: #3aa875;
}

.summary-bottom-bar .summary-total {
  background-color: #e8f5e8;
  padding: 10px 15px;
  border-radius: 4px;
  border: 1px solid #c8e6c9;
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 150px;
  justify-content: flex-end;
}

.summary-bottom-bar .total-label {
  font-size: 14px;
  color: #1b5e20;
  font-weight: 600;
  white-space: nowrap;
}

.summary-bottom-bar .total-price {
  font-size: 24px;
  color: #0d47a1;
  font-weight: 700;
  white-space: nowrap;
}

/* Empty state */
.summary-bottom-bar .empty-summary {
  color: #666;
  font-style: italic;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  text-align: left;
  flex: 1;
  min-width: 0;
}

/* Expand toggle button */
.expand-toggle {
  background: none;
  border: none;
  color: #42b983;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 5px 10px;
  border-radius: 4px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 5px;
}

.expand-toggle:hover {
  background-color: #f0f8ff;
}

/* Arrow icon for expand toggle */
.expand-toggle::before {
  content: '\25B2';
  font-size: 10px;
}

.expand-toggle.expanded::before {
  content: '\25BC';
}

/* Make header and body visible when collapsed */
.summary-bottom-bar:not(.expanded) .summary-header {
  margin-bottom: 5px;
  padding-bottom: 5px;
}

.summary-bottom-bar:not(.expanded) .summary-body {
  display: flex;
  align-items: center;
}

.summary-bottom-bar:not(.expanded) .summary-list {
  background: none;
  border: none;
  padding: 0;
  max-height: 30px;
  overflow: hidden;
}

.summary-bottom-bar:not(.expanded) .summary-item {
  padding: 0;
  border: none;
  background: none;
}

.summary-bottom-bar:not(.expanded) .summary-device-info {
  display: none;
}

.summary-bottom-bar:not(.expanded) .summary-price {
  font-size: 14px;
  color: #333;
  font-weight: normal;
}

.summary-bottom-bar:not(.expanded) .summary-total {
  min-width: auto;
  padding: 5px 10px;
  flex-shrink: 0;
}

.summary-bottom-bar:not(.expanded) .total-price {
  font-size: 18px;
}

/* Always show expand button */
.summary-bottom-bar:not(.expanded) .expand-toggle {
  display: flex;
}

/* Always show expand button when expanded */
.summary-bottom-bar.expanded .expand-toggle {
  display: flex;
}

/* Ensure good spacing when expanded */
.summary-bottom-bar.expanded .summary-items-row {
  align-items: flex-start;
}

.summary-bottom-bar.expanded .summary-list-container {
  max-height: 200px;
}

.summary-bottom-bar.expanded .summary-total-container {
  align-items: flex-start;
}

.summary-bottom-bar.expanded .summary-total {
  min-width: 200px;
  padding: 10px 15px;
}

.summary-bottom-bar.expanded .total-price {
  font-size: 24px;
}

h3 {
  margin-bottom: 20px;
  color: #333;
  font-size: 20px;
  text-align: center;
  padding-bottom: 15px;
  border-bottom: 2px solid #42b983;
}

h4 {
  margin: 20px 0 15px 0;
  color: #555;
  font-size: 16px;
  font-weight: 600;
}

h5 {
  margin: 0 0 8px 0;
  color: #333;
  font-size: 14px;
  font-weight: 600;
}

.section {
  margin-bottom: 30px;
}

/* Loading styles */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.spinner {
  border: 4px solid rgba(0,0,0,0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #42b983;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Room list styles */
.room-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.room-item {
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border-left: 4px solid #42b983;
  cursor: pointer;
  transition: all 0.2s;
}

.room-item:hover {
  background-color: #e9ecef;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.room-item.selected {
  background-color: #e3f2fd;
  border-left-color: #2196f3;
}

.room-function {
  margin: 0 0 10px 0;
  color: #666;
  font-size: 13px;
  line-height: 1.4;
}

.key-needs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.need-tag {
  padding: 4px 8px;
  background-color: #e9ecef;
  color: #495057;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

/* Device tree styles */
.device-tree {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.category-node {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  overflow: hidden;
  background-color: #fafafa;
}

.category-header {
  background-color: #f0f8ff;
  padding: 12px 15px;
  border-bottom: 1px solid #e9ecef;
}

.category-header h5 {
  margin: 0;
  color: #333;
  font-size: 14px;
  font-weight: 600;
}

.type-groups {
  padding: 10px 15px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.type-node {
  border: 1px solid #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  background-color: #fff;
}

.type-header {
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #f8f9fa;
  cursor: pointer;
  transition: background-color 0.2s;
  user-select: none;
}

.type-header:hover {
  background-color: #e9ecef;
}

.type-icon {
  font-size: 10px;
  color: #6c757d;
  transition: transform 0.2s;
}

.type-name {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  flex: 1;
}

.type-count {
  font-size: 11px;
  color: #6c757d;
  font-weight: 500;
  background-color: #e9ecef;
  padding: 2px 6px;
  border-radius: 10px;
}

.device-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 15px;
}

.device-item {
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.device-item:hover {
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.device-item.selected {
  background-color: #fff3cd;
  border-color: #ffeeba;
}

.device-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.device-name {
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.device-reason {
  margin: 0 0 8px 0;
  color: #666;
  font-size: 12px;
  line-height: 1.4;
}

.device-position {
  font-size: 11px;
  color: #888;
  font-style: italic;
}

/* Product selection styles */
.product-selection {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e9ecef;
}

.product-selection label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #555;
}

.product-dropdown {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 12px;
  background-color: #fff;
  cursor: pointer;
  transition: border-color 0.2s;
}

.product-dropdown:focus {
  outline: none;
  border-color: #42b983;
  box-shadow: 0 0 0 2px rgba(66, 185, 131, 0.25);
}

.selected-product-info {
  margin-top: 8px;
  padding: 8px;
  background-color: #e8f5e8;
  border: 1px solid #c8e6c9;
  border-radius: 4px;
  font-size: 11px;
  line-height: 1.4;
}

.product-details {
  color: #2e7d32;
}

.product-details strong {
  color: #1b5e20;
}

/* Control section styles */
.control-section {
  padding: 15px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  margin-bottom: 20px;
}

/* Upload section styles */
.upload-section,
.ai-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.upload-section h4,
.ai-section h4 {
  margin-top: 0;
  margin-bottom: 10px;
  color: #333;
  font-size: 14px;
}

.file-input {
  padding: 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background-color: #fff;
  font-size: 13px;
}

.btn {
  padding: 10px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #42b983;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #3aa875;
}

.upload-error,
.analysis-error {
  color: #dc3545;
  font-size: 13px;
  margin-top: 5px;
}

.upload-success,
.analysis-success {
  color: #28a745;
  font-size: 13px;
  margin-top: 5px;
  word-break: break-all;
}

.upload-success a {
  color: #007bff;
  text-decoration: none;
}

.upload-success a:hover {
  text-decoration: underline;
}

.ai-phase-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
}

.phase-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.phase-thinking {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffc107;
}

.phase-generating {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #28a745;
}

.thinking-panel {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
}

.thinking-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 10px;
  background-color: #f8f9fa;
  cursor: pointer;
  font-size: 12px;
  color: #6c757d;
  user-select: none;
}

.thinking-content {
  padding: 8px 10px;
  font-size: 11px;
  color: #6c757d;
  max-height: 200px;
  overflow-y: auto;
  background-color: #fafbfc;
  border-top: 1px solid #dee2e6;
  line-height: 1.5;
}

.thinking-content p,
.generating-content p {
  margin: 0 0 6px;
}

.thinking-content p:last-child,
.generating-content p:last-child {
  margin-bottom: 0;
}

.thinking-content code,
.generating-content code {
  background-color: #e9ecef;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 10px;
}

.thinking-content pre,
.generating-content pre {
  background-color: #e9ecef;
  padding: 6px 8px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 10px;
  margin: 4px 0;
}

.thinking-content strong,
.generating-content strong {
  font-weight: 600;
}

.generating-preview {
  border: 1px solid #c3e6cb;
  border-radius: 6px;
  overflow: hidden;
}

.generating-header {
  padding: 4px 10px;
  background-color: #d4edda;
  font-size: 12px;
  color: #155724;
  font-weight: 500;
}

.generating-content {
  padding: 6px 10px;
  font-size: 11px;
  color: #333;
  max-height: 150px;
  overflow-y: auto;
  margin: 0;
  background-color: #f8f9fa;
  line-height: 1.5;
}

.toggle-icon {
  font-size: 10px;
}

.control-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  color: #555;
  user-select: none;
}

.control-checkbox input[type="checkbox"] {
  margin-right: 8px;
  transform: scale(1.2);
  accent-color: #42b983;
}

.control-checkbox:hover {
  color: #333;
}

/* Device type checkboxes styles */
.device-type-checkboxes {
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  padding-right: 5px;
}

.device-type-checkbox {
  display: flex;
  align-items: center;
  padding: 5px 0;
  border-bottom: 1px solid #f0f0f0;
}

.device-type-checkbox:last-child {
  border-bottom: none;
}

/* Scrollbar styles for device type checkboxes */
.device-type-checkboxes::-webkit-scrollbar {
  width: 4px;
}

.device-type-checkboxes::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.device-type-checkboxes::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.device-type-checkboxes::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Delete device button */
.delete-device-btn {
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.delete-device-btn:hover {
  background-color: #c82333;
}

/* Add device controls */
.add-device-controls {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
}

.device-type-select {
  padding: 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background-color: #fff;
  font-size: 14px;
}

.stage-click-instruction {
  background-color: #e3f2fd;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #bbdefb;
  margin-top: 10px;
}

.stage-click-instruction p {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #1565c0;
}

/* Secondary button style */
.btn-secondary {
  background-color: #6c757d;
  color: white;
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: background-color 0.2s;
}

.btn-secondary:hover {
  background-color: #5a6268;
}

/* Zoom controls */
.zoom-controls {
  position: absolute;
  bottom: 15px;
  right: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: rgba(255, 255, 255, 0.95);
  padding: 8px 12px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  z-index: 10;
}

.zoom-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #fff;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.zoom-btn:hover {
  background-color: #42b983;
  color: white;
  border-color: #42b983;
}

.zoom-level {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  min-width: 45px;
  text-align: center;
}

.reset-btn {
  width: auto;
  padding: 0 12px;
  font-size: 13px;
  font-weight: normal;
}
</style>